import { Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from '../../shared/services/message.service';
import { DatePipe, NgClass } from '@angular/common';
import { Subscription, timer } from 'rxjs';
import { FileService } from '../../shared/services/file.service';
import { LeftSidebarComponent } from '../../shared/left-sidebar/left-sidebar.component';
import { ChatModalComponent } from '../../shared/chat-modal/chat-modal.component';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [ReactiveFormsModule, NgClass, FormsModule, DatePipe, LeftSidebarComponent, ChatModalComponent],
  templateUrl: 'dashboard.component.html',
})
export class DashboardComponent implements OnInit {
  @ViewChild('messageInput') messageInput?: ElementRef<HTMLTextAreaElement>;
  private messageService = inject(MessageService);
  private fileService = inject(FileService);
  unansweredMessages = signal<any[]>([]);
  messages = signal<any[]>([]);
  number = signal('Izaberi broj');
  providerCode = signal('');
  providerPrefix = signal('');
  identity = signal('');
  profile = signal('');
  note = signal('');
  channels = signal<any[]>([]);
  selectedChannel = signal<any | null>(null);
  messageText = signal('');
  currentUser: number | null = null;
  currentProviderCode: string | null = null;
  currentPrefix: string | null = null;
  currentProviderCodeId: number | null = null;
  private subscription: Subscription = new Subscription();

  ngOnInit() {
    this.loadMessages();
    this.subscription = timer(0, 50000).subscribe(() => {
      this.loadMessages();
      this.loadUserMessages();
    });
  }

  loadMessages() {
    this.messageService.getUnansweredMessages().subscribe({
      next: (data) => this.unansweredMessages.set(data.messages),
      error: (err) => console.error(err),
    });
  }

  onUnansweredMessageClick(
    userId: number,
    providerCodeId: number,
    providerCode: string,
    prefix: string,
  ) {
    this.currentUser = userId;
    this.currentProviderCodeId = providerCodeId;
    this.currentProviderCode = providerCode;
    this.currentPrefix = prefix;
    this.loadUserMessages(true);
    this.messageText.set('');
    setTimeout(() => this.messageInput?.nativeElement.focus());
  }

  onMessageInputEnter(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey) {
      return;
    }
    keyboardEvent.preventDefault();
    this.sendMessage();
  }

  sendMessage(mine = true) {
    const message = this.messageText().trim();
    if (!message || this.currentUser == null || this.currentProviderCodeId == null) {
      return;
    }
    this.messageService
      .sendMessage(this.currentUser, this.currentProviderCodeId, message, mine)
      .subscribe({
        next: (data) => {
          this.messages.update((messages) => [...messages, data.message]);
          this.messageText.set('');
          this.messageInput?.nativeElement.focus();
          if (mine) {
            this.refreshInboxAndAdvanceIfAnswered();
          }
        },
        error: (err) => console.error(err),
      });
  }

  // A chat can still owe more than one reply (client sent several messages in a row),
  // so we can't just remove it locally after a single reply - re-check the backend's
  // unanswered balance and only move on once it's actually gone from that list.
  private refreshInboxAndAdvanceIfAnswered() {
    const answeredUserId = this.currentUser;
    const answeredProviderCodeId = this.currentProviderCodeId;
    const previousIndex = this.unansweredMessages().findIndex(
      (m) => m.number_id === answeredUserId && m.provider_code_id === answeredProviderCodeId,
    );
    this.messageService.getUnansweredMessages().subscribe({
      next: (data) => {
        const updated = data.messages;
        this.unansweredMessages.set(updated);

        const stillUnanswered = updated.some(
          (m: any) => m.number_id === answeredUserId && m.provider_code_id === answeredProviderCodeId,
        );
        if (stillUnanswered) {
          return;
        }

        if (updated.length === 0) {
          this.resetSelection();
          return;
        }

        const next = updated[Math.min(Math.max(previousIndex, 0), updated.length - 1)];
        this.onUnansweredMessageClick(next.number_id, next.provider_code_id, next.code, next.prefix_code);
      },
      error: (err) => console.error(err),
    });
  }

  private resetSelection() {
    this.currentUser = null;
    this.currentProviderCodeId = null;
    this.currentProviderCode = null;
    this.currentPrefix = null;
    this.messages.set([]);
    this.number.set('Izaberi broj');
    this.providerCode.set('');
    this.providerPrefix.set('');
    this.identity.set('');
    this.profile.set('');
    this.note.set('');
    this.channels.set([]);
    this.messageText.set('');
  }

  loadUserMessages(userSelected = false) {
    if (this.currentUser && this.currentProviderCodeId) {
      this.messageService.getUserMessages(this.currentUser, this.currentProviderCodeId).subscribe({
        next: (data) => {
          this.messages.set(data.messages);
          this.number.set(data.number);
          this.providerCode.set(data.code);
          this.providerPrefix.set(data.prefix);
          if (userSelected) {
            this.identity.set(data.identity);
            this.profile.set(data.profile);
            this.note.set(data.note);
            this.channels.set(data.channels);
          }
        },
        error: (err) => console.error(err),
      });
    }
  }

  openChannelChat(channel: any) {
    if (channel.id === this.currentProviderCodeId) {
      return;
    }
    this.selectedChannel.set(channel);
  }

  closeChannelChat() {
    this.selectedChannel.set(null);
  }

  saveFile() {
    if (this.currentProviderCodeId != null) {
      this.fileService
        .saveFile(
          this.number().toString(),
          this.currentProviderCodeId,
          this.note().toString(),
          this.profile().toString(),
          this.identity().toString(),
        )
        .subscribe({
          next: (data) => {
            debugger;
          },
        });
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
